import { useContext, useEffect } from 'react'
import { cn } from '@/utlis/cn'
import SidebarMenu from '@/pages/SessionPage/internal/sidebar/SidebarMenu'
import { UserContext, UserContextType } from '@/UserContext'
import UserList from '@/pages/SessionPage/internal/sidebar/UserList'
import { isThinStripSidebar, useViewMode } from '@/pages/SessionPage/internal/shared/useViewMode'
import ChatRoom, { ChatboxTabTypes } from '@/pages/ChatRoom/ChatRoom'
import { logger } from '@/services/Logger'
import DiagonalTabs from '@/components/DiagonalTabs'
import CommunicationIcon from '@/CustomIcons/CommunicationIcon'
import * as Constants from '@/services/Constants'
import { Toolbar } from '../toolbar/Toolbar'
import useDimensionObserver from '@/services/useDimensionObserver'
import { Badge } from '@/UIComponents2/Badge'
import { Collapsible } from '@/UIComponents2/Collapsible'
import { Trigger } from '@/utlis/Trigger'
import DragHandleIcon from '@/CustomIcons/DragHandleIcon'
import useChatRoomController from '@/pages/ChatRoom/useChatRoomController'
import { useTranslation } from 'react-i18next'
import { RoomContext, RoomContextType } from '@/RoomContext'
import OSXTitleBar from '@/components/OSXTitleBar'

const log = logger.createChildLogger({ name: 'Sidebar' })

type SidebarProps = {
    reserveToolbarSpace?: boolean
    chatController: ReturnType<typeof useChatRoomController>
}
const Sidebar = (props: SidebarProps) => {
    const { state: userState } = useContext(UserContext) as UserContextType
    const { state: roomState } = useContext(RoomContext) as RoomContextType

    const viewMode = useViewMode()
    const { width, height, nodeRef } = useDimensionObserver()

    const isSidebarMode = viewMode.galleryHidden || viewMode.sidebarPoppedOut

    const heightStyle = viewMode.sidebarPoppedOut
        ? { height: '100%' }
        : { height: `calc(100% - ${Constants.title_bar_height})` } // when sidebar is popped out, there is no title bar (esp. Windows)

    const thinStripSidebar = isThinStripSidebar(viewMode)

    const bg = cn(
        'bg-[rgba(255,255,255,0.08)] sm:rounded-[24px] shadow-glow border border-white/10',
        viewMode.sidebarPoppedOut && 'bg-neutral-900',
        viewMode.sidebarCollapsed && 'px-1 sm:rounded-[16px]'
    )
    return (
        <div
            ref={nodeRef}
            className={cn(
                'flex flex-col w-full h-dvh min-h-0 md:py-[10px] pr-0 md:pr-[8px] justify-self-end',
                thinStripSidebar && bg
            )}
        >
            {thinStripSidebar && !viewMode.sidebarPoppedOut && <OSXTitleBar />}
            {thinStripSidebar && <DragHandle />}
            <div
                className={cn(
                    'relative flex flex-col p-2 w-full h-full min-h-0',
                    !thinStripSidebar && bg,
                    isSidebarMode && 'pb-0'
                )}
                style={heightStyle}
            >
                {!thinStripSidebar && isSidebarMode && !viewMode.sidebarPoppedOut && (
                    <OSXTitleBar noBg className="-m-2 mb-0" />
                )}
                <SidebarMenu chatController={props.chatController} />
                {!viewMode.sidebarCollapsed && (
                    <>
                        <UserList />
                        {(roomState.roomTierPolicy?.allowAudienceChat || userState.clientType !== 'listenonly') && (
                            <ChatRoomAccordion
                                sidebarMode={isSidebarMode}
                                chatController={props.chatController}
                                containerWidth={width}
                            />
                        )}
                    </>
                )}
                {props.reserveToolbarSpace && <Toolbar inSidebar chatController={props.chatController} />}
            </div>
        </div>
    )
}

export const chatTrigger = new Trigger<boolean>()
function ChatRoomAccordion({
    chatController,
    containerWidth,
    sidebarMode,
}: {
    chatController: ReturnType<typeof useChatRoomController>
    containerWidth: number
    sidebarMode: boolean
}) {
    const { t } = useTranslation()
    const margin_w = 28

    const unreadMessagesCt = chatController.unreadMessagesCt

    useEffect(() => {
        const h = (open: boolean) => {
            chatController.setIsOpen(open)
        }
        chatTrigger.on('trigger', h)
        return () => {
            chatTrigger.off('trigger', h)
        }
    }, [])

    const hideTitle = sidebarMode && !chatController.isOpen

    return (
        <div className={!hideTitle ? 'h-[48px]' : ''}>
            <Collapsible
                title={
                    !hideTitle && (
                        <span className="flex items-center gap-[10px]">
                            <CommunicationIcon style={{ fontSize: '1.3rem' }} /> {t('Chat Room')}
                            {unreadMessagesCt > 0 && (
                                <Badge className="" noAbsolute>
                                    {unreadMessagesCt}
                                </Badge>
                            )}
                        </span>
                    )
                }
                open={chatController.isOpen}
                onOpenChange={chatController.setIsOpen}
                className={cn(
                    'absolute z-20 bottom-[10px] left-[8px] right-[8px] rounded-[14px]',
                    !hideTitle && 'border-muse-toplit-low bg-neutral-700/30 backdrop-blur-[40px]'
                )}
                triggerClassName="h-[40px] flex items-center px-2 t-base-medium"
                reverseOpenIndicator
            >
                <div className="p-[4px]">
                    <DiagonalTabs
                        widthFitTabs
                        initialIndex={0}
                        containerWidth={containerWidth ? containerWidth - margin_w : 345}
                        containerHeight={500}
                        onTabChange={(tab) => chatController.setCurrentTab(tab as unknown as ChatboxTabTypes)}
                        childrenByTabIndex={[
                            <ChatRoom chatController={chatController} currentTab="messages" />,
                            <ChatRoom chatController={chatController} currentTab="files" />,
                        ]}
                        tabs={[
                            { id: 'messages', label: t('Messages') },
                            { id: 'files', label: t('Files') },
                        ]}
                    />
                </div>
            </Collapsible>
        </div>
    )
}

function DragHandle() {
    return (
        <div className="h-[30px] w-full app-window-drag-region flex items-center justify-center cursor-grab">
            <DragHandleIcon className="rotate-90" />
        </div>
    )
}

export default Sidebar
